/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Multi-tab org sync broadcast — singleton BroadcastChannel giúp 1 tab (writer)
 * chịu trách nhiệm fetch + ghi IndexedDB; các tab còn lại (listener) chỉ patch
 * UI in-memory qua channel, tránh duplicate API calls.
 *
 * Cơ chế:
 *  1. Writer yêu cầu quyền: broadcast `sync-request` → đợi 100ms.
 *     - Nếu không có tab nào response (channel rỗng / không peer) → claim writer.
 *     - Nếu có tab khác đang là writer (heartbeat còn sống) → thành listener.
 *  2. Writer fetch + ghi IndexedDB xong → broadcast `sync-complete` với version mới.
 *  3. Listener nhận `sync-complete` → patch UI + update localVersion.
 *  4. Writer broadcast `sync-claim` mỗi 10s làm heartbeat. Nếu listener không
 *     thấy heartbeat trong 30s → tự promote thành writer cho lần sync kế tiếp.
 *
 * Fallback: nếu BroadcastChannel không hỗ trợ (Safari cũ) → dùng `storage` event.
 */

const CHANNEL_NAME = 'toppion-org-sync';
const HEARTBEAT_INTERVAL_MS = 10_000;
const HEARTBEAT_TIMEOUT_MS = 30_000;
const CLAIM_WAIT_MS = 100;

/** Module key — chỉ dùng nội bộ orgSyncBroadcast.ts. Caller từ organizationIndexedDBUtils.ts
 *  dùng type OrganizationSyncModule riêng (cùng 4 giá trị literal). Hai type structural-compatible. */
export type OrgSyncModuleKey = 'employees' | 'departments' | 'positions' | 'rest';

export type OrgSyncMessage =
    | { type: 'sync-request'; module: OrgSyncModuleKey; remoteVersion?: string; requesterId: string; timestamp: number }
    | { type: 'sync-complete'; module: OrgSyncModuleKey; version: string | null; writerId: string; timestamp: number }
    | { type: 'sync-claim'; writerId: string; module?: OrgSyncModuleKey; timestamp: number };

type Listener = (msg: OrgSyncMessage) => void;

/** ID duy nhất cho mỗi tab — dùng để so sánh writer/listener. */
export const TAB_ID: string = (() => {
    try {
        // sessionStorage tách biệt giữa các tab → mỗi tab có ID riêng.
        const key = '__toppion_org_tab_id__';
        const existing = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
        if (existing) return existing;
        const id = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, id);
        return id;
    } catch {
        return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }
})();

interface PeerWriter {
    id: string;
    lastHeartbeat: number;
}

class OrgSyncBroadcast {
    private channel: BroadcastChannel | null = null;
    private listeners: Set<Listener> = new Set();
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    private peerWriters: Map<string, PeerWriter> = new Map();
    private currentWriterModule: OrgSyncModuleKey | null = null;
    private fallbackStorageHandler: ((e: StorageEvent) => void) | null = null;
    private fallbackStorageKey = `${CHANNEL_NAME}:fallback`;

    constructor() {
        if (typeof window === 'undefined') return;
        if (typeof BroadcastChannel !== 'undefined') {
            this.channel = new BroadcastChannel(CHANNEL_NAME);
            this.channel.onmessage = (e) => this.handleMessage(e.data as OrgSyncMessage);
        } else {
            // Fallback: dùng localStorage 'storage' event. Mỗi tab push message vào
            // một key ephemeral → các tab khác nhận qua window.storage event.
            this.fallbackStorageHandler = (e: StorageEvent) => {
                if (e.key !== this.fallbackStorageKey || !e.newValue) return;
                try {
                    const msg = JSON.parse(e.newValue) as OrgSyncMessage;
                    this.handleMessage(msg);
                } catch {
                    // ignore parse error
                }
            };
            window.addEventListener('storage', this.fallbackStorageHandler);
        }

        // Garbage collect peer writers quá hạn mỗi 5s.
        if (typeof setInterval !== 'undefined') {
            setInterval(() => this.gcPeers(), 5_000);
        }
    }

    // ---------- public API ----------

    /** Subscribe message từ các tab khác (không nhận message do chính tab này gửi). */
    subscribe(fn: Listener): () => void {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    }

    /**
     * Yêu cầu quyền writer cho 1 module.
     * - Resolve true  → tab này là writer, được phép gọi API + ghi IndexedDB.
     * - Resolve false → tab khác đang là writer, tab này đợi `sync-complete` qua subscribe.
     *
     * Nếu trong CLAIM_WAIT_MS không có peer writer nào response → auto-claim.
     */
    async acquireWriterLock(module: OrgSyncModuleKey, remoteVersion?: string): Promise<boolean> {
        // Nếu mình đã là writer của module này → ok luôn.
        if (this.currentWriterModule === module) return true;

        // Nếu có peer writer còn sống → nhường.
        if (this.hasAlivePeerWriter()) return false;

        this.send({ type: 'sync-request', module, remoteVersion, requesterId: TAB_ID, timestamp: Date.now() });

        await new Promise((r) => setTimeout(r, CLAIM_WAIT_MS));

        // Sau khi đợi, kiểm tra lại peer writer. Nếu có peer response (qua sync-request
        // không, nhưng heartbeat) hoặc qua logic cùng lúc — ta so sánh TAB_ID với peer
        // để tránh 2 tab cùng claim. TAB_ID nhỏ hơn thì thắng.
        if (this.hasAlivePeerWriter()) {
            const peerIds = Array.from(this.peerWriters.keys()).filter((id) => id !== TAB_ID);
            if (peerIds.some((id) => id < TAB_ID)) return false;
        }

        this.currentWriterModule = module;
        this.startHeartbeat();
        return true;
    }

    /** Writer gọi hàm này sau khi ghi IndexedDB xong để broadcast cho listener patch UI. */
    broadcastSyncComplete(module: OrgSyncModuleKey, version: string | null): void {
        this.send({
            type: 'sync-complete',
            module,
            version,
            writerId: TAB_ID,
            timestamp: Date.now(),
        });
        this.currentWriterModule = null;
        this.stopHeartbeat();
    }

    /** Trả về true nếu tab hiện tại đang giữ writer lock. */
    isWriter(module: OrgSyncModuleKey): boolean {
        return this.currentWriterModule === module;
    }

    /** Cleanup khi tab unload. */
    dispose(): void {
        this.stopHeartbeat();
        this.listeners.clear();
        if (this.channel) {
            this.channel.close();
            this.channel = null;
        }
        if (this.fallbackStorageHandler && typeof window !== 'undefined') {
            window.removeEventListener('storage', this.fallbackStorageHandler);
            this.fallbackStorageHandler = null;
        }
    }

    // ---------- internal ----------

    private send(msg: OrgSyncMessage): void {
        if (this.channel) {
            try {
                this.channel.postMessage(msg);
            } catch {
                // channel closed → ignore
            }
        } else if (typeof localStorage !== 'undefined') {
            try {
                // storage event không fire ở tab ghi → ok
                localStorage.setItem(this.fallbackStorageKey, JSON.stringify(msg));
            } catch {
                // ignore quota
            }
        }
    }

    private handleMessage(msg: OrgSyncMessage): void {
        if (!msg || typeof msg !== 'object') return;

        if (msg.type === 'sync-request') {
            // Mình đang là writer → không nhường, nhưng update peer list.
            if (msg.requesterId !== TAB_ID) {
                this.peerWriters.set(msg.requesterId, { id: msg.requesterId, lastHeartbeat: Date.now() });
            }
        } else if (msg.type === 'sync-claim') {
            if (msg.writerId !== TAB_ID) {
                this.peerWriters.set(msg.writerId, { id: msg.writerId, lastHeartbeat: Date.now() });
            }
        } else if (msg.type === 'sync-complete') {
            if (msg.writerId !== TAB_ID) {
                this.peerWriters.set(msg.writerId, { id: msg.writerId, lastHeartbeat: Date.now() });
            }
            // sync-complete phát cho tất cả listener (trừ writer).
        }

        this.listeners.forEach((fn) => {
            try {
                fn(msg);
            } catch (e) {
                // ignore listener error
            }
        });
    }

    private startHeartbeat(): void {
        if (this.heartbeatTimer) return;
        this.heartbeatTimer = setInterval(() => {
            if (this.currentWriterModule) {
                this.send({
                    type: 'sync-claim',
                    writerId: TAB_ID,
                    module: this.currentWriterModule,
                    timestamp: Date.now(),
                });
            }
        }, HEARTBEAT_INTERVAL_MS);
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    private hasAlivePeerWriter(): boolean {
        const now = Date.now();
        for (const peer of this.peerWriters.values()) {
            if (now - peer.lastHeartbeat < HEARTBEAT_TIMEOUT_MS) return true;
        }
        return false;
    }

    private gcPeers(): void {
        const now = Date.now();
        for (const [id, peer] of this.peerWriters.entries()) {
            if (now - peer.lastHeartbeat >= HEARTBEAT_TIMEOUT_MS) {
                this.peerWriters.delete(id);
            }
        }
    }
}

let _instance: OrgSyncBroadcast | null = null;

/** Singleton accessor — dùng chung 1 instance trong toàn bộ app. */
export function getOrgSyncBroadcast(): OrgSyncBroadcast {
    if (!_instance) {
        _instance = new OrgSyncBroadcast();
        if (typeof window !== 'undefined') {
            // Cleanup khi tab đóng.
            window.addEventListener('beforeunload', () => {
                _instance?.dispose();
                _instance = null;
            });
        }
    }
    return _instance;
}
